'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import type { ConversationSummaryDTO } from '@jas/shared';
import {
  deleteAllConversations,
  deleteConversation,
  listConversations,
} from '@/lib/conversations';

// First-page cache survives close/open cycles so opening the drawer N
// times in quick succession fires one network call, not N. The cache
// is rendered immediately and a background revalidate kicks in past
// the staleness window.
const CACHE_FRESH_MS = 30_000;

interface FirstPageCache {
  at: number;
  data: ConversationSummaryDTO[];
  nextCursor: string | null;
}

/**
 * Slide-from-left history drawer for signed-in users. Triggered by the
 * navbar's history icon. Lists past conversations, newest activity
 * first, with per-row delete. Read-only this PR — clicking a row is a
 * no-op until the chat-mode integration PR adds the resume flow.
 *
 * Anonymous users never see this drawer (the trigger button is hidden
 * by AuthButton's conditional rendering).
 *
 * Pagination is cursor-based against /api/conversations?cursor=...
 * — "Load more" appends the next page rather than replacing the list,
 * matching the ChatGPT / Claude UX pattern.
 */
export function HistoryDrawer({
  open,
  onClose,
  onResume,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Triggered when the user clicks a conversation row. The parent
   * (voice-panel) owns the actual resume mechanics: fetching the
   * detail, seeding turns, starting a new session, and replaying the
   * past transcript to the agent via sendContextualUpdate. Optional —
   * the drawer renders read-only if omitted.
   */
  onResume?: (id: string) => void;
}) {
  const { authenticated, getAccessToken } = usePrivy();
  const [conversations, setConversations] = useState<ConversationSummaryDTO[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Two-step "delete all" — first click sets confirm=true and surfaces
  // a destructive-styled button; second click fires the API. Auto-
  // reverts if the user doesn't follow through within a few seconds
  // so the dangerous state isn't sticky.
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  // Cache survives close/open cycles and is invalidated either by a
  // delete (we mutate cacheRef in onDelete) or by exceeding
  // CACHE_FRESH_MS staleness. Sign-out resets it via the effect below.
  const cacheRef = useRef<FirstPageCache | null>(null);

  const fetchPage = useCallback(
    async (cursor: string | null) => {
      if (!authenticated) return;
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('no access token');
        const params = new URLSearchParams();
        if (cursor) params.set('cursor', cursor);
        const res = await listConversations(token, params.toString() || undefined);
        setConversations((prev) =>
          cursor === null ? res.conversations : [...(prev ?? []), ...res.conversations],
        );
        setNextCursor(res.nextCursor);
        if (cursor === null) {
          cacheRef.current = {
            at: Date.now(),
            data: res.conversations,
            nextCursor: res.nextCursor,
          };
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [authenticated, getAccessToken],
  );

  // Reset cache on sign-out so a different user's conversations don't
  // leak into the next sign-in.
  useEffect(() => {
    if (!authenticated) {
      cacheRef.current = null;
      setConversations(null);
      setNextCursor(null);
    }
  }, [authenticated]);

  // On open: SWR-lite — show cached data instantly if fresh, otherwise
  // refetch. Lets the user open / close the drawer rapidly without
  // hammering the API. The 30s window is short enough that stale data
  // doesn't accumulate; conversations created elsewhere appear after
  // the next staleness expiry.
  useEffect(() => {
    if (!open) return;
    const cached = cacheRef.current;
    if (cached && Date.now() - cached.at < CACHE_FRESH_MS) {
      setConversations(cached.data);
      setNextCursor(cached.nextCursor);
      return;
    }
    setConversations(null);
    setNextCursor(null);
    void fetchPage(null);
  }, [open, fetchPage]);

  // Close on Escape — standard drawer affordance.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Auto-revert the destructive confirmation if the user doesn't
  // click through within 4s.
  useEffect(() => {
    if (!confirmDeleteAll) return;
    const t = window.setTimeout(() => setConfirmDeleteAll(false), 4000);
    return () => window.clearTimeout(t);
  }, [confirmDeleteAll]);

  const onDeleteAll = useCallback(async () => {
    if (!authenticated) return;
    setDeletingAll(true);
    // Snapshot for rollback.
    const prevList = conversations;
    const prevCache = cacheRef.current;
    // Optimistic: clear the visible list + cache immediately.
    setConversations([]);
    setNextCursor(null);
    cacheRef.current = { at: Date.now(), data: [], nextCursor: null };
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('no access token');
      await deleteAllConversations(token);
      setConfirmDeleteAll(false);
    } catch (err) {
      // Rollback.
      setConversations(prevList);
      cacheRef.current = prevCache;
      setError((err as Error).message);
    } finally {
      setDeletingAll(false);
    }
  }, [authenticated, conversations, getAccessToken]);

  const onDelete = useCallback(
    async (id: string) => {
      if (!authenticated) return;
      setBusyId(id);
      // Snapshot for rollback. Optimistic removal keeps the UI snappy
      // (delete feels instant) but a failed API call restores the row
      // and surfaces the error — the alternative (pessimistic delete)
      // would make every delete feel laggy. We also have to remember
      // to rebuild the cache so re-opening doesn't show the stale row.
      const prevList = conversations;
      const prevCache = cacheRef.current;
      setConversations((cur) => cur?.filter((c) => c.id !== id) ?? null);
      if (cacheRef.current) {
        cacheRef.current = {
          ...cacheRef.current,
          data: cacheRef.current.data.filter((c) => c.id !== id),
        };
      }
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('no access token');
        await deleteConversation(token, id);
      } catch (err) {
        // Rollback both the visible list and the cache.
        setConversations(prevList);
        cacheRef.current = prevCache;
        setError((err as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [authenticated, conversations, getAccessToken],
  );

  if (!authenticated) return null;

  return (
    <>
      {/* Backdrop. Click closes. Fades in/out — pointer-events-none
          while closed so it doesn't intercept clicks under the navbar. */}
      <div
        className={`fixed inset-0 z-40 bg-surface/80 backdrop-blur-sm transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer. Slides from -translate-x-full (offscreen-left) to 0. */}
      <aside
        className={`fixed top-0 left-0 z-50 h-dvh w-[320px] max-w-[80vw] bg-surface border-r border-outline-variant transition-transform duration-300 flex flex-col ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-outline-variant">
          <p className="font-mono text-[10px] tracking-widest text-primary">HISTORY</p>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] tracking-widest text-outline hover:text-on-surface-variant transition-colors"
            aria-label="Close history"
          >
            CLOSE
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-3">
          {error && (
            <p className="mx-3 mb-3 font-mono text-[10px] tracking-widest text-error">
              {error}
            </p>
          )}

          {conversations === null && loading && <SkeletonRows />}

          {conversations !== null && conversations.length === 0 && <EmptyState />}

          {conversations !== null && conversations.length > 0 && (
            <ul className="space-y-0.5">
              {conversations.map((c) => (
                <Row
                  key={c.id}
                  conversation={c}
                  busy={busyId === c.id}
                  onResume={onResume ? () => onResume(c.id) : undefined}
                  onDelete={() => void onDelete(c.id)}
                />
              ))}
            </ul>
          )}

          {nextCursor && (
            <button
              type="button"
              onClick={() => void fetchPage(nextCursor)}
              disabled={loading}
              className="mt-4 mx-3 w-[calc(100%-1.5rem)] font-mono text-[10px] tracking-widest text-outline hover:text-on-surface-variant transition-colors border border-outline-variant py-2 disabled:opacity-50"
            >
              {loading ? 'LOADING…' : 'LOAD MORE'}
            </button>
          )}
        </div>

        {/* Destructive footer — only renders when there's history to
            delete. Two-step confirmation: first click flips to the
            armed state, second click fires. Auto-reverts after 4s
            (see effect above) so the dangerous state isn't sticky if
            the user wanders off. */}
        {conversations !== null && conversations.length > 0 && (
          <div className="border-t border-outline-variant px-3 py-3">
            {confirmDeleteAll ? (
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => void onDeleteAll()}
                  disabled={deletingAll}
                  className="flex-1 font-mono text-[10px] tracking-widest text-on-primary bg-error-text hover:opacity-90 px-3 py-2 disabled:opacity-50"
                >
                  {deletingAll ? 'DELETING…' : 'CONFIRM DELETE ALL'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteAll(false)}
                  disabled={deletingAll}
                  className="font-mono text-[10px] tracking-widest text-outline hover:text-on-surface-variant px-3 py-2"
                >
                  CANCEL
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDeleteAll(true)}
                className="w-full font-mono text-[10px] tracking-widest text-outline hover:text-error-text transition-colors py-2"
              >
                DELETE ALL HISTORY
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function Row({
  conversation,
  busy,
  onResume,
  onDelete,
}: {
  conversation: ConversationSummaryDTO;
  busy: boolean;
  onResume?: () => void;
  onDelete: () => void;
}) {
  const ts = new Date(conversation.updatedAt);
  const dateLabel = ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  // Whole row is the resume target; delete button is a stop-propagation
  // child so a misclick on the delete affordance doesn't also trigger
  // a resume.
  const Tag: 'button' | 'div' = onResume ? 'button' : 'div';
  return (
    <li className="group relative">
      <Tag
        type={onResume ? 'button' : undefined}
        onClick={onResume}
        disabled={onResume ? busy : undefined}
        className={`w-full text-left px-3 py-2.5 transition-colors ${
          onResume
            ? 'hover:bg-surface-container-low active:bg-surface-container cursor-pointer disabled:cursor-default disabled:opacity-50'
            : ''
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-on-surface truncate">
              {conversation.title ?? 'Untitled'}
            </p>
            <p className="mt-1 font-mono text-[10px] tracking-widest text-outline">
              {conversation.mode.toUpperCase()} · {conversation.messageCount} MSG · {dateLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete();
            }}
            disabled={busy}
            className="font-mono text-[10px] tracking-widest text-outline hover:text-error opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50 shrink-0 mt-0.5"
            aria-label={`Delete ${conversation.title ?? 'untitled conversation'}`}
          >
            {busy ? '…' : 'DEL'}
          </button>
        </div>
      </Tag>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="mx-3 mt-6 border border-dashed border-outline-variant px-4 py-6">
      <p className="font-mono text-[10px] tracking-widest text-primary mb-2">NO HISTORY YET</p>
      <p className="text-xs text-on-surface-variant leading-relaxed">
        Your past conversations with Satoshi will live here. Sign in, start a session, and
        come back any time to pick up where you left off.
      </p>
    </div>
  );
}

function SkeletonRows() {
  // Three placeholder rows while the first page loads. Prevents the
  // drawer from rendering empty and shifting once data lands.
  return (
    <ul className="space-y-1 px-3" aria-hidden>
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="h-14 bg-surface-container-low animate-pulse"
          style={{ animationDelay: `${i * 100}ms` }}
        />
      ))}
    </ul>
  );
}
